import React, { useEffect } from "react";
import { Box, Button, Step, StepLabel, Stepper, ThemeProvider, createTheme } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { useAppDispatch } from "../../../../shared/hooks/sharedHooks";
import MainDetailsStep from "./MainDetailsStep";
import ShippingAndOtherDetailsStep from "./ShippingAndOtherDetailsStep";
import PreviewStep from "./PreviewStep";
import { CurrencyCode, ProductConditionCode } from "../../../../common/models/CommonModels";
import { ChangeOffer } from "../../models/models";
import { createOffer } from "../../customerSentOffersSlice";
import { toWarrantyPeriodMonths } from "../../../../shared/models/models";

const steps = [
  {
    title: "Main details",
    component: <MainDetailsStep />
  },
  {
    title: "Shipping and other details",
    component: <ShippingAndOtherDetailsStep />
  },
  {
    title: "Preview",
    component: <PreviewStep />
  }
];

const CreateOffer = () => {
  const dispatch = useAppDispatch();
  const formMethods = useForm<ChangeOffer>();
  const navigate = useNavigate();
  const [activeStepIndex, setActiveStepIndex] = React.useState(0);

  const { id } = useParams();

  const handleCreateOffer = async () => {
    const model = formMethods.getValues();

    model.productAlternativeNumber = model.productAlternativeNumber || undefined;
    model.unitPrice = model.unitPrice ? { id: CurrencyCode.eur, value: Number(model.unitPrice) } : undefined;
    model.quantity = model.quantity ? Number(model.quantity) : undefined;
    model.productConditionCode = Number(model.productConditionCode);
    model.warrantyPeriodMonths = model.warrantyPeriod ? toWarrantyPeriodMonths(model.warrantyPeriod) : undefined;
    model.warrantyPeriod = undefined;
    model.documentFile = model.documentFile;
    model.imageFile = model.imageFile;

    await dispatch(createOffer(model));
    navigate("/trading/rfq-feed");
  };

  const handleNext = async () => {
    await formMethods.trigger();

    if (!formMethods.formState.isValid) {
      return;
    }

    if (activeStepIndex === steps.length - 1) {
      handleCreateOffer();
      return;
    }

    setActiveStepIndex((prevActiveStepIndex) => prevActiveStepIndex + 1);
  };

  const handleBack = () => {
    if (activeStepIndex === 0) {
      navigate("/trading/rfq-feed");
      return;
    }

    setActiveStepIndex((prevActiveStepIndex) => prevActiveStepIndex - 1);
  };

  const init = async () => {
    const initialValues = {
      quotationRequestId: id || "",
      productConditionCode: ProductConditionCode.new,
    };
    formMethods.reset(initialValues);
  };

  useEffect(() => {
    init();
  }, []);

  const theme = createTheme({
    components: {
      MuiStepper: {
        styleOverrides: {
          root: {
            ".MuiStepIcon-root.Mui-active": {
              color: "#333",
              fontWeight: 500
            },
            ".MuiStepLabel-label": {
              fontFamily: "'Work Sans', 'Open Sans', sans-serif"
            }
          }
        }
      }
    }
  });

  return (
    <ThemeProvider theme={theme}>
      <FormProvider {...formMethods}>
        <form id="createRfqForm">
          <Box className="grid grid-cols-4 gap-5 p-5">
            <Box className="col-span-2">
              <Stepper activeStep={activeStepIndex} className="mt-4">
                {steps.map((step, index) => (
                  <Step key={index}>
                    <StepLabel className="text-14-medium">
                      {step.title}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>

              <React.Fragment>{steps[activeStepIndex].component}</React.Fragment>
            </Box>

            <Box className="mt-16">
              <Button variant="contained" className="w-full" onClick={handleNext}>
                {activeStepIndex === steps.length - 1 ? "Send this offer" : "Next step"}
              </Button>

              <Box className="mt-3">
                <Button variant="outlined" className="w-full" onClick={handleBack}>
                  {activeStepIndex === 0 ? "Cancel" : "Back"}
                </Button>
              </Box>
            </Box>
          </Box>
        </form>
      </FormProvider>
    </ThemeProvider>
  );
};

export default CreateOffer;
